/** Server detail page showing comprehensive server information. */
import React, { useEffect, useState } from "react";
import {
  Container,
  Paper,
  Typography,
  Box,
  Grid,
  Chip,
  Button,
  Divider,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
} from "@mui/material";
import {
  ArrowBack as ArrowBackIcon,
  Computer as ComputerIcon,
  Memory as MemoryIcon,
  Storage as StorageIcon,
  Phone as PhoneIcon,
  AccessTime as TimeIcon,
  Settings as ConfigIcon,
  Download as DownloadIcon,
} from "@mui/icons-material";
import { useNavigate, useParams } from "react-router-dom";
import { serversApi } from "../api/client";
import { Server, ServerStatus, ServerType } from "../types";
import { format } from "date-fns";

/** Server detail page component. */
export default function ServerDetail() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [server, setServer] = useState<Server | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  /** Fetch server details. */
  useEffect(() => {
    if (!id) {
      setError("No server ID provided");
      setLoading(false);
      return;
    }

    const fetchServer = async () => {
      try {
        setLoading(true);
        const response = await serversApi.get(id);
        setServer(response.data);
      } catch (err: any) {
        console.error("Failed to fetch server:", err);
        setError(err.response?.data?.detail || "Failed to load server details");
      } finally {
        setLoading(false);
      }
    };

    fetchServer();
  }, [id]);

  /** Get status chip color. */
  const getStatusColor = (status: ServerStatus) => {
    switch (status) {
      case ServerStatus.ONLINE:
        return "success";
      case ServerStatus.DEGRADED:
        return "warning";
      case ServerStatus.OFFLINE:
        return "error";
      case ServerStatus.DRAINING:
        return "info";
      default:
        return "default";
    }
  };

  /** Get server type icon. */
  const getServerTypeIcon = (type: ServerType) => {
    switch (type) {
      case ServerType.KAMAILIO:
        return <PhoneIcon />;
      case ServerType.FREESWITCH:
        return <ComputerIcon />;
      case ServerType.CGRATES:
        return <StorageIcon />;
      default:
        return <ComputerIcon />;
    }
  };

  /** Format date safely. */
  const formatDate = (dateString: string | null) => {
    if (!dateString) return "Never";
    try {
      return format(new Date(dateString), "yyyy-MM-dd HH:mm:ss");
    } catch {
      return "Invalid date";
    }
  };

  /** Handle download join script. */
  const handleDownloadScript = async () => {
    if (!server) return;
    try {
      const response = await serversApi.getJoinScript(server.id);
      const blob = new Blob([response.data], { type: "text/plain" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `join-server-${server.id}.sh`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      setError(err.response?.data?.detail || "Failed to download join script");
    }
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error || !server) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="error" sx={{ mb: 3 }}>
          {error || "Server not found"}
        </Alert>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate("/dashboard")}
          variant="outlined"
        >
          Back to Dashboard
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate("/dashboard")}
          sx={{ mb: 2 }}
        >
          Back to Dashboard
        </Button>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
          {getServerTypeIcon(server.server_type)}
          <Typography variant="h4" component="h1">
            {server.hostname}
          </Typography>
          <Chip
            label={server.status}
            color={getStatusColor(server.status) as any}
            size="small"
          />
        </Box>
        <Typography variant="body1" color="text.secondary">
          {server.server_type} Server • ID: {server.id}
        </Typography>
      </Box>

      {/* Overview Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Active Calls
              </Typography>
              <Typography variant="h4">
                {server.active_calls.toLocaleString()}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                CPU Usage
              </Typography>
              <Typography variant="h4">
                {server.cpu_percent !== null ? `${server.cpu_percent.toFixed(1)}%` : "N/A"}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Memory Usage
              </Typography>
              <Typography variant="h4">
                {server.memory_percent !== null ? `${server.memory_percent.toFixed(1)}%` : "N/A"}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Config Version
              </Typography>
              <Typography variant="h4">
                v{server.config_version}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        {/* Server Details */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Server Details
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <List dense>
              <ListItem>
                <ListItemIcon>
                  <ComputerIcon />
                </ListItemIcon>
                <ListItemText
                  primary="Hostname"
                  secondary={server.hostname}
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <StorageIcon />
                </ListItemIcon>
                <ListItemText
                  primary="Server Type"
                  secondary={server.server_type}
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <ConfigIcon />
                </ListItemIcon>
                <ListItemText
                  primary="Configuration Version"
                  secondary={server.config_version}
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <TimeIcon />
                </ListItemIcon>
                <ListItemText
                  primary="Created"
                  secondary={formatDate(server.created_at)}
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <TimeIcon />
                </ListItemIcon>
                <ListItemText
                  primary="Last Updated"
                  secondary={formatDate(server.updated_at)}
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <MemoryIcon />
                </ListItemIcon>
                <ListItemText
                  primary="Last Heartbeat"
                  secondary={formatDate(server.last_heartbeat)}
                />
              </ListItem>
            </List>
          </Paper>
        </Grid>

        {/* Actions */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Actions
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <Button
                variant="outlined"
                startIcon={<DownloadIcon />}
                onClick={handleDownloadScript}
                fullWidth
              >
                Download Join Script
              </Button>
              <Button
                variant="outlined"
                color="error"
                onClick={() => navigate("/dashboard")}
                fullWidth
              >
                Back to Dashboard
              </Button>
            </Box>

            <Typography variant="body2" color="text.secondary" sx={{ mt: 3 }}>
              Additional server management features will be available in future updates.
            </Typography>
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
}
